/** @typedef {import('headlight-configurable-application').Pict} Pict */

const PictProvider = require('pict-provider');

const THROUGH_PAGE_SIZE = 500;

/**
 * @typedef {object} ThroughFilterConfig
 * @property {string} Through - bridge entity (singular; the route pluralizes it), e.g. 'OrganizationMaterialJoin'.
 * @property {string} ThroughKey - bridge column matched against the live anchor value, e.g. 'IDMaterial'.
 * @property {string} ThroughSelect - bridge column projected into the IN-list, e.g. 'IDOrganization'.
 * @property {string} [FromField] - manifest address of the anchor value (the picked upstream sibling).
 * @property {string} Field - the filtered picker's own column the INN clause targets, e.g. 'IDOrganization'.
 * @property {('map'|'query')} Strategy - 'map' lazy-loads the whole bridge once; 'query' fetches per-anchor slices.
 * @property {string} [ThroughFilter] - extra static bridge scope, ANDed in. No default: soft-deleted rows are
 *   excluded server-side (foxhound adds the predicate for any schema carrying a Deleted column).
 * @property {string} [ThroughProjection] - read projection (default 'Lite').
 */

/**
 * ThroughScopeResolver — generic indirect ("chained") filter resolver. Lets a
 * picker narrow by a relationship that has no direct FK on the filtered entity,
 * by hopping through a bridge table. A DynamicFilters entry of the shape
 * {@link ThroughFilterConfig} narrows `Field` to the `ThroughSelect` values
 * found in `Through` where `ThroughKey` equals the live anchor at `FromField`.
 *
 * Both strategies resolve to a SYNCHRONOUS id-set read at filter-assembly time
 * (Select2Util.applyDynamicFilters is synchronous):
 *
 *   - 'map'   — lazy-load the whole bridge once (per Through+scope), build
 *               Map<ThroughKey → Set<ThroughSelect>> + the union of all selects,
 *               cache. For small bridge tables. Primed on app load for every
 *               map-Through on the loaded form. With no anchor, the union set is
 *               returned (the "only rows that participate at all" relevance case).
 *   - 'query' — fetch only `Through WHERE ThroughKey = anchor` on demand and
 *               cache the slice keyed by the anchor value. For large bridge
 *               tables where a wholesale load is too costly (e.g.
 *               OrganizationMaterialJoin, ~52k rows). Primed by Select2Util's
 *               transport (the async seam) right before the dependent read.
 *
 * Fail-open like SpecScopeResolver: an unloaded slice / empty result returns
 * null, so the dispatch omits the clause and the picker shows its full set
 * (recoverable via Show All regardless).
 *
 * Consumers:
 *   resolver.primeMapScope(filter)             // app load (map only)
 *   resolver.primeThroughSlice(filter, anchor) // transport, await (query only)
 *   resolver.getThroughIDs(filter, anchor)     // sync: Set<number> | null
 */
class ThroughScopeResolver extends PictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		/** @type {Pict} */
		this.pict;
		/** @type {Record<string, { Loaded: boolean, LoadPromise: Promise<void>|null, Forward: Map<string, Set<number>>, AllSelects: Set<number> }>} */
		this._MapScopes = {};
		/** @type {Record<string, { Slices: Map<string, Set<number>>, Pending: Map<string, Promise<void>> }>} */
		this._QueryScopes = {};
	}

	/**
	 * Cache key for a Through filter — the bridge entity, the directed key→select
	 * pair, and the static scope. Same for both strategies (a filter only uses one).
	 *
	 * @param {ThroughFilterConfig} pFilter
	 * @return {string}
	 */
	_cacheKey(pFilter)
	{
		return `${pFilter.Through}|${pFilter.ThroughKey}>${pFilter.ThroughSelect}|${pFilter.ThroughFilter || ''}`;
	}

	/**
	 * Synchronous allowed-id lookup for a Through filter at an anchor value.
	 * Returns Set<number> when resolved, or null (fail-open) when not yet loaded
	 * or the config is incomplete.
	 *
	 *   - map, no anchor  → union of all selects (relevance: rows that participate).
	 *   - map, anchor     → the anchor's select set (empty Set when the anchor maps to none).
	 *   - query, no anchor→ null (query needs an anchor to have fetched a slice).
	 *   - query, anchor   → the cached slice, or null until primeThroughSlice resolves.
	 *
	 * @param {ThroughFilterConfig} pFilter
	 * @param {string|number} pAnchorValue
	 * @return {Set<number>|null}
	 */
	getThroughIDs(pFilter, pAnchorValue)
	{
		if (!pFilter || !pFilter.Through || !pFilter.ThroughKey || !pFilter.ThroughSelect)
		{
			return null;
		}
		const tmpAnchor = (pAnchorValue === null || pAnchorValue === undefined) ? '' : String(pAnchorValue).trim();
		const tmpHasAnchor = (tmpAnchor !== '' && tmpAnchor !== '0');
		if (pFilter.Strategy === 'map')
		{
			const tmpScope = this._MapScopes[this._cacheKey(pFilter)];
			if (!tmpScope || !tmpScope.Loaded)
			{
				return null;
			}
			if (!tmpHasAnchor)
			{
				return tmpScope.AllSelects;
			}
			return tmpScope.Forward.get(tmpAnchor) || new Set();
		}
		// query
		if (!tmpHasAnchor)
		{
			return null;
		}
		const tmpScope = this._QueryScopes[this._cacheKey(pFilter)];
		const tmpSlice = tmpScope ? tmpScope.Slices.get(tmpAnchor) : undefined;
		return tmpSlice || null;
	}

	/**
	 * Ensure the query-strategy slice for (filter, anchor) is loaded + cached.
	 * Idempotent and de-duped per anchor (concurrent calls share one fetch). No-op
	 * for the map strategy (those prime on app load). Awaited by the transport
	 * before the dependent picker reads, so getThroughIDs resolves synchronously.
	 *
	 * @param {ThroughFilterConfig} pFilter
	 * @param {string|number} pAnchorValue
	 * @return {Promise<void>}
	 */
	async primeThroughSlice(pFilter, pAnchorValue)
	{
		if (!pFilter || pFilter.Strategy !== 'query' || !pFilter.Through || !pFilter.ThroughKey || !pFilter.ThroughSelect)
		{
			return;
		}
		const tmpAnchor = (pAnchorValue === null || pAnchorValue === undefined) ? '' : String(pAnchorValue).trim();
		if (tmpAnchor === '' || tmpAnchor === '0')
		{
			return;
		}
		const tmpKey = this._cacheKey(pFilter);
		const tmpScope = this._QueryScopes[tmpKey] || (this._QueryScopes[tmpKey] = { Slices: new Map(), Pending: new Map() });
		if (tmpScope.Slices.has(tmpAnchor))
		{
			return;
		}
		if (tmpScope.Pending.has(tmpAnchor))
		{
			return tmpScope.Pending.get(tmpAnchor);
		}
		const tmpPromise = this._loadSlice(pFilter, tmpAnchor)
			.then((pSet) => { tmpScope.Slices.set(tmpAnchor, pSet); })
			.catch((pErr) =>
			{
				this.pict?.log?.warn(`[ThroughScopeResolver] slice load failed ${tmpKey}=${tmpAnchor}: ${pErr?.message || pErr}`);
				tmpScope.Slices.set(tmpAnchor, new Set());
			})
			.finally(() => { tmpScope.Pending.delete(tmpAnchor); });
		tmpScope.Pending.set(tmpAnchor, tmpPromise);
		return tmpPromise;
	}

	/**
	 * @param {ThroughFilterConfig} pFilter
	 * @param {string} pAnchor
	 * @return {Promise<Set<number>>}
	 */
	async _loadSlice(pFilter, pAnchor)
	{
		const tmpRoute = `${pFilter.Through}s/${pFilter.ThroughProjection || 'Lite'}`;
		const tmpClauses = [ `FBV~${pFilter.ThroughKey}~EQ~${encodeURIComponent(pAnchor)}` ];
		if (pFilter.ThroughFilter)
		{
			tmpClauses.unshift(pFilter.ThroughFilter);
		}
		const tmpRows = await this._readAllPages(tmpRoute, tmpClauses.join('~'));
		return new Set(this._distinctPositive(tmpRows.map((pRow) => pRow[pFilter.ThroughSelect])));
	}

	/**
	 * Ensure the map-strategy scope for a filter is fully loaded + indexed. Safe
	 * to call repeatedly; de-dupes via the in-flight LoadPromise.
	 *
	 * @param {ThroughFilterConfig} pFilter
	 * @return {Promise<void>}
	 */
	async primeMapScope(pFilter)
	{
		if (!pFilter || pFilter.Strategy !== 'map' || !pFilter.Through || !pFilter.ThroughKey || !pFilter.ThroughSelect)
		{
			return;
		}
		const tmpKey = this._cacheKey(pFilter);
		const tmpExisting = this._MapScopes[tmpKey];
		if (tmpExisting && tmpExisting.Loaded)
		{
			return;
		}
		if (tmpExisting && tmpExisting.LoadPromise)
		{
			return tmpExisting.LoadPromise;
		}
		const tmpScope = this._MapScopes[tmpKey] = { Loaded: false, LoadPromise: null, Forward: new Map(), AllSelects: new Set() };
		tmpScope.LoadPromise = this._loadMap(pFilter, tmpScope)
			.then(() => { tmpScope.Loaded = true; })
			.catch((pErr) =>
			{
				this.pict?.log?.warn(`[ThroughScopeResolver] map load failed ${tmpKey}: ${pErr?.message || pErr}`);
				tmpScope.Loaded = true;
			});
		return tmpScope.LoadPromise;
	}

	/**
	 * @param {ThroughFilterConfig} pFilter
	 * @param {{ Forward: Map<string, Set<number>>, AllSelects: Set<number> }} pScope
	 * @return {Promise<void>}
	 */
	async _loadMap(pFilter, pScope)
	{
		const tmpRoute = `${pFilter.Through}s/${pFilter.ThroughProjection || 'Lite'}`;
		// Sorted, not merely scoped: this pages the WHOLE bridge, and a paged read with no ORDER BY can
		// repeat or skip rows between pages. It also supplies the filter stanza the paged route requires —
		// meadow-endpoints exposes no bare `s/:Begin/:Cap`.
		const tmpFilter = pFilter.ThroughFilter || `FSF~${pFilter.ThroughKey}~ASC~0`;
		const tmpRows = await this._readAllPages(tmpRoute, tmpFilter);
		for (const tmpRow of tmpRows)
		{
			const tmpKeyVal = (tmpRow[pFilter.ThroughKey] === null || tmpRow[pFilter.ThroughKey] === undefined) ? '' : String(tmpRow[pFilter.ThroughKey]).trim();
			const tmpSelectVal = parseInt(tmpRow[pFilter.ThroughSelect], 10);
			if (tmpKeyVal === '' || !(tmpSelectVal > 0))
			{
				continue;
			}
			let tmpSet = pScope.Forward.get(tmpKeyVal);
			if (!tmpSet)
			{
				tmpSet = new Set();
				pScope.Forward.set(tmpKeyVal, tmpSet);
			}
			tmpSet.add(tmpSelectVal);
			pScope.AllSelects.add(tmpSelectVal);
		}
	}

	/**
	 * App-load hook — prime every map-strategy Through on the loaded form so the
	 * synchronous getThroughIDs resolves on the first render. PictProvider
	 * defaults AutoLoadDataWithApp true, so this participates without host changes.
	 * Failure is non-fatal (fail-open). Query-strategy filters are not primed here
	 * (they load per-anchor in the transport).
	 *
	 * @param {(error?: Error) => void} fCallback
	 */
	onAfterLoadDataAsync(fCallback)
	{
		return super.onAfterLoadDataAsync((pError) =>
		{
			if (pError)
			{
				return fCallback(pError);
			}
			this._primeScopesFromForm().catch((pErr) =>
			{
				this.pict?.log?.warn(`[ThroughScopeResolver] app-load prime failed: ${pErr?.message || pErr}`);
			}).finally(() => fCallback());
		});
	}

	/**
	 * @return {Promise<void>}
	 */
	async _primeMapScopesFromForm()
	{
		const tmpSeen = new Set();
		const tmpMapFilters = [];
		for (const tmpFilter of this._collectThroughFilters())
		{
			if (tmpFilter.Strategy !== 'map')
			{
				continue;
			}
			const tmpKey = this._cacheKey(tmpFilter);
			if (tmpSeen.has(tmpKey))
			{
				continue;
			}
			tmpSeen.add(tmpKey);
			tmpMapFilters.push(tmpFilter);
		}
		await Promise.all(tmpMapFilters.map((pFilter) => this.primeMapScope(pFilter)));
	}

	/**
	 * Prime both map and query scopes for the loaded form.
	 *
	 * @return {Promise<void>}
	 */
	async _primeScopesFromForm()
	{
		await Promise.all([ this._primeMapScopesFromForm(), this._primeQueryScopesFromForm() ]);
	}

	/**
	 * Prime the query-strategy slice for every query-Through filter on the form at
	 * its current anchor value. Called on app load (pre-filled anchors) and via
	 * primeQueryScopesFromForm whenever the cascade changes an anchor.
	 *
	 * @return {Promise<void>}
	 */
	async _primeQueryScopesFromForm()
	{
		const tmpJobs = [];
		for (const tmpFilter of this._collectThroughFilters())
		{
			if (tmpFilter.Strategy !== 'query' || !tmpFilter.FromField)
			{
				continue;
			}
			const tmpAnchor = this.pict.manifest.getValueByHash(this.pict, tmpFilter.FromField);
			tmpJobs.push(this.primeThroughSlice(tmpFilter, tmpAnchor));
		}
		await Promise.all(tmpJobs);
	}

	/**
	 * Public fire-and-forget entry the cascade calls when an anchor changes, so the
	 * dependent picker's query slice is warm before it is opened. Swallows errors.
	 *
	 * @return {Promise<void>}
	 */
	primeQueryScopesFromForm()
	{
		return this._primeQueryScopesFromForm().catch((pErr) =>
		{
			this.pict?.log?.warn(`[ThroughScopeResolver] query prime failed: ${pErr?.message || pErr}`);
		});
	}

	/**
	 * Collect every Through filter entry across the loaded form's descriptors.
	 *
	 * @return {Array<ThroughFilterConfig>}
	 */
	_collectThroughFilters()
	{
		const tmpDescriptors = this.pict?.views?.PictFormMetacontroller?.manifestDescription?.Descriptors;
		const tmpOut = [];
		if (!tmpDescriptors)
		{
			return tmpOut;
		}
		for (const tmpDescriptor of Object.values(tmpDescriptors))
		{
			const tmpFilters = tmpDescriptor?.PictForm?.DynamicFilters;
			if (!Array.isArray(tmpFilters))
			{
				continue;
			}
			for (const tmpFilter of tmpFilters)
			{
				if (tmpFilter && tmpFilter.Through && tmpFilter.ThroughKey && tmpFilter.ThroughSelect)
				{
					tmpOut.push(tmpFilter);
				}
			}
		}
		return tmpOut;
	}

	/**
	 * Page through a filtered meadow read until exhausted. Mirrors
	 * SpecScopeResolver — plain FilteredTo paging so the offline client can serve
	 * from synced entity data.
	 *
	 * @param {string} pEntityRoute - pluralized route incl. projection, e.g. 'OrganizationMaterialJoins/Lite'.
	 * @param {string} pFilter - meadow filter stanza.
	 * @return {Promise<Array<any>>}
	 */
	async _readAllPages(pEntityRoute, pFilter)
	{
		const tmpRows = [];
		let tmpOffset = 0;
		for (;;)
		{
			const tmpPage = await this._getJSON(`${pEntityRoute}/FilteredTo/${pFilter}/${tmpOffset}/${THROUGH_PAGE_SIZE}`);
			if (!Array.isArray(tmpPage) || tmpPage.length === 0)
			{
				break;
			}
			for (const tmpRow of tmpPage)
			{
				tmpRows.push(tmpRow);
			}
			if (tmpPage.length < THROUGH_PAGE_SIZE)
			{
				break;
			}
			tmpOffset += THROUGH_PAGE_SIZE;
		}
		return tmpRows;
	}

	/**
	 * @param {Array<any>} pValues
	 * @return {Array<number>} distinct positive integers.
	 */
	_distinctPositive(pValues)
	{
		const tmpSet = new Set();
		for (const tmpValue of pValues)
		{
			const tmpNumber = parseInt(tmpValue, 10);
			if (tmpNumber > 0)
			{
				tmpSet.add(tmpNumber);
			}
		}
		return Array.from(tmpSet);
	}

	/**
	 * @param {string} pUrl
	 * @return {Promise<any>}
	 */
	_getJSON(pUrl)
	{
		return new Promise((resolve, reject) =>
		{
			this.pict.HeadlightRestClient.getJSON(pUrl, (pError, pBody) =>
			{
				if (pError)
				{
					return reject(pError);
				}
				resolve(pBody);
			});
		});
	}
}

module.exports = ThroughScopeResolver;
